import type {
  MixedFeedItem,
  SponsoredFeedItem,
} from "../types/sponsored";

export type SponsoredInjectionConfig = {
  firstInsertionAfterOrganicPosts: number;
  subsequentOrganicIntervals: readonly number[];
};

function positiveInteger(value: number, fallback: number) {
  return Number.isInteger(value) && value > 0 ? value : fallback;
}

export function injectSponsoredItems<T extends { id: string }>(
  posts: readonly T[],
  sponsoredItems: readonly SponsoredFeedItem[],
  config: SponsoredInjectionConfig
): MixedFeedItem<T>[] {
  if (posts.length === 0) return [];

  const organicItems: MixedFeedItem<T>[] = posts.map((post) => ({
    type: "organic",
    key: `organic:${post.id}`,
    post,
  }));
  if (sponsoredItems.length === 0) return organicItems;

  const firstGap = positiveInteger(config.firstInsertionAfterOrganicPosts, 4);
  if (posts.length < firstGap) return organicItems;

  const intervals = config.subsequentOrganicIntervals.length
    ? config.subsequentOrganicIntervals.map((gap) => positiveInteger(gap, firstGap))
    : [firstGap];
  const result: MixedFeedItem<T>[] = [];
  let sponsoredIndex = 0;
  let organicSinceSponsored = 0;
  let requiredOrganic = firstGap;

  for (const post of posts) {
    result.push({ type: "organic", key: `organic:${post.id}`, post });
    organicSinceSponsored += 1;

    if (
      sponsoredIndex < sponsoredItems.length &&
      organicSinceSponsored >= requiredOrganic
    ) {
      result.push(sponsoredItems[sponsoredIndex]);
      sponsoredIndex += 1;
      organicSinceSponsored = 0;
      requiredOrganic = intervals[(sponsoredIndex - 1) % intervals.length];
    }
  }

  return result;
}

export function isSafeSponsoredUrl(value: string | undefined): value is string {
  if (!value) return false;
  try {
    const parsed = new URL(value);
    return (
      (parsed.protocol === "https:" || parsed.protocol === "http:") &&
      parsed.hostname.length > 0 &&
      !parsed.username &&
      !parsed.password
    );
  } catch {
    return false;
  }
}
